import type { AxiosError, AxiosRequestConfig } from 'axios';
import { useCallback } from 'react';
import { type UseQueryOptions, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KEYS, repository } from '@repositories/Repository';
import type ITenantMonthlyBillingRepository from '@repositories/tenantMonthlyBillingRepository/ITenantMonthlyBillingRepository';
import type {
  TenantBillingId,
  TenantBillingsFilters,
  TenantMonthlyBillingDTO,
} from '@repositories/tenantMonthlyBillingRepository/tenantMonthlyBilling.types';
import { TenantMonthlyBillingModel } from '@repositories/tenantMonthlyBillingRepository/models/TenantMonthlyBillingModel';

const tenantBillingRepo = repository.get<ITenantMonthlyBillingRepository>(KEYS.TENANT_MONTHLY_BILLING_REPOSITORY);

export const tenantBillingKeys = {
  all: ['tenantBillings'] as const,
  lists: () => [...tenantBillingKeys.all, 'list'] as const,
  list: (filter?: TenantBillingsFilters) => [...tenantBillingKeys.lists(), { ...filter }] as const,
  listInfinite: (filter?: TenantBillingsFilters) => [...tenantBillingKeys.list(filter), 'infinite'] as const,
  details: () => [...tenantBillingKeys.all, 'detail'] as const,
  detail: (filter: TenantBillingId) => [...tenantBillingKeys.details(), filter] as const,
};

export const useGetTenantBilling = (
  billingId: TenantBillingId,
  options?: Omit<UseQueryOptions<TenantMonthlyBillingDTO, AxiosError, TenantMonthlyBillingModel>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectTenantBilling = useCallback(
    (data: TenantMonthlyBillingDTO) => new TenantMonthlyBillingModel(data),
    [],
  );
  return useQuery<TenantMonthlyBillingDTO, AxiosError, TenantMonthlyBillingModel>({
    queryKey: tenantBillingKeys.detail(billingId),
    queryFn: () => tenantBillingRepo.getOne(billingId),
    select: selectTenantBilling,
    ...options,
    cacheTime: 5 * 60 * 1000,
    staleTime: 4 * 60 * 1000,
    enabled: !!billingId && (options?.enabled !== undefined ? options.enabled : true),
  });
};

export const useGetTenantBillingsInfinite = (filter?: TenantBillingsFilters) =>
  useInfiniteQuery({
    queryKey: tenantBillingKeys.listInfinite(filter),
    queryFn: async ({ pageParam = 0 }) => {
      const result = await tenantBillingRepo.getList({ ...filter, page: pageParam });
      return {
        ...result,
        content: result.content.map(dto => new TenantMonthlyBillingModel(dto)),
      };
    },
    getNextPageParam: lastPage => (lastPage.last ? undefined : lastPage.number + 1),
  });

export const useUploadTenantBillingAttachments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      month,
      files,
      config,
    }: {
      tenantId: string;
      month: string;
      files: File[];
      config?: AxiosRequestConfig;
    }) => {
      await tenantBillingRepo.fileUpload(tenantId, month, files, config);
      return { tenantId, month };
    },
    onSuccess: ({ tenantId, month }) => {
      queryClient.invalidateQueries({ queryKey: tenantBillingKeys.detail({ id: tenantId, month }) });
    },
  });
};

export const useDeleteTenantBillingAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, month, fileId }: { tenantId: string; month: string; fileId: string }) => {
      await tenantBillingRepo.fileDelete(tenantId, month, fileId);
      return { tenantId, month };
    },
    onSuccess: ({ tenantId, month }) => {
      queryClient.invalidateQueries({ queryKey: tenantBillingKeys.detail({ id: tenantId, month }) });
    },
  });
};
